import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import _ from 'lodash';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { AcceptSpecificCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { LinesChangedAction } from 'src/app/store/samwin/samwin-lines/samwin-lines.actions';
import { SamwinLineStatusRemoteDto } from 'src/generated/signalR/samwin-line-status-remote-dto';

export interface IPeerInfo {
   peerId: string;
   peerName: string;
}

export interface IShortCallInfo extends IPeerInfo {
   callId: number;
}

export interface SamwinLinesStateModel {
   sourceLineStatus?: SamwinLineStatusRemoteDto;
   destinationLineStatus?: SamwinLineStatusRemoteDto;
   callIdBeingAnswered: number;
   isAnswering: boolean;
}

@State<SamwinLinesStateModel>({
   name: 'samwinLines',
   defaults: {
      sourceLineStatus: null,
      destinationLineStatus: null,
      callIdBeingAnswered: null,
      isAnswering: false,
   },
})
@Injectable()
export class SamwinLinesState {
   @Selector([SamwinLinesState])
   public static isSourceLineAssigned(state: SamwinLinesStateModel): boolean {
      return (state?.sourceLineStatus && LineStateEnum.valueOf(state?.sourceLineStatus?.lineState)?.isRinging) ?? false;
   }

   @Selector([SamwinLinesState])
   public static isSourceLineConnected(state: SamwinLinesStateModel): boolean {
      return (state?.sourceLineStatus && LineStateEnum.valueOf(state?.sourceLineStatus?.lineState)?.isCallInProgress) ?? false;
   }

   @Selector([SamwinLinesState.isSourceLineAssigned, SamwinLinesState.isSourceLineConnected])
   public static isSourceLineAssignedOrConnected(isSourceLineConnected: boolean, isSourceLineAssigned: boolean): boolean {
      return isSourceLineAssigned || isSourceLineConnected;
   }

   // This selector should not be moved to the SamwinLineSelector to prevent circular dependencies
   @Selector([SamwinLinesState, SamwinLinesState.isSourceLineAssigned, SamwinLinesState.isSourceLineConnected])
   public static getActiveOrRingingCallId(state: SamwinLinesStateModel, isRinging: boolean, isInProgress: boolean): number {
      if (isRinging || isInProgress) {
         return state.sourceLineStatus.callId;
      }
      return null;
   }

   @Selector([SamwinLinesState])
   public static getSourceLineCallId(state: SamwinLinesStateModel): number {
      return state?.sourceLineStatus?.callId;
   }

   @Selector([SamwinLinesState])
   public static getSourceLine(state: SamwinLinesStateModel): SamwinLineStatusRemoteDto {
      return state?.sourceLineStatus;
   }

   @Selector([SamwinLinesState])
   public static isAnswering(state: SamwinLinesStateModel): boolean {
      return state.isAnswering;
   }

   @Selector([SamwinLinesState.getSourceLine])
   public static getIsConnectedSince(sourceState: SamwinLineStatusRemoteDto): Date {
      return sourceState?.connectedSince;
   }

   @Action(LinesChangedAction)
   public linesChanged(ctx: StateContext<SamwinLinesStateModel>, action: LinesChangedAction) {
      const currentState = ctx.getState();
      let newIsAnswering = currentState.isAnswering;
      let newCallIdBeingAnswered = currentState.callIdBeingAnswered;

      // with shared skills, linestate is set to null for a brief moment (this caused headsets to go OnHook)
      const sLine = !_.isNil(action.sourceLine) ? LineStateEnum.valueOf(action.sourceLine?.lineState) : LineStateEnum.UNKNOWN;
      if (_.isNil(currentState?.sourceLineStatus) && sLine === LineStateEnum.ACCEPTED) {
         newIsAnswering = true;
      }

      // reset flag when source line goes idle
      if (
         !_.isNil(currentState.sourceLineStatus) &&
         LineStateEnum.valueOf(currentState.sourceLineStatus?.lineState) !== LineStateEnum.IDLE &&
         sLine === LineStateEnum.IDLE
      ) {
         newCallIdBeingAnswered = null;
      }

      if (
         !_.isNil(currentState.sourceLineStatus) &&
         LineStateEnum.valueOf(currentState.sourceLineStatus?.lineState) === LineStateEnum.CONNECTED &&
         sLine === LineStateEnum.IDLE
      ) {
         newIsAnswering = false;
      }

      ctx.patchState({
         destinationLineStatus: action.destinationLine,
         sourceLineStatus: action.sourceLine,
         isAnswering: newIsAnswering,
         callIdBeingAnswered: newCallIdBeingAnswered,
      });
   }

   @Action(AcceptSpecificCallAction)
   public AcceptSpecificCallAction(ctx: StateContext<SamwinLinesStateModel>, action: AcceptSpecificCallAction) {
      console.log(`Accept call ${action.callId}`);

      ctx.patchState({
         callIdBeingAnswered: action.callId,
         isAnswering: true,
      });
   }
}
